import React from 'react';

/**
 * Visual gauge showing Mapbox API usage against the 50k free tier limit
 */
const MapboxUsageGauge = ({ current, limit = 50000, dailyAverage = 0, daysTracked = 0 }) => {
  const percentage = Math.min((current / limit) * 100, 100);

  // Determine color based on usage percentage
  const getColor = () => {
    if (percentage >= 95) return { stroke: '#ffb4ab', bg: 'bg-error/10', text: 'text-error' };
    if (percentage >= 80) return { stroke: '#ffb887', bg: 'bg-primary/10', text: 'text-primary' };
    if (percentage >= 50) return { stroke: '#ffb783', bg: 'bg-primary/10', text: 'text-primary-fixed-dim' };
    return { stroke: '#4adf9f', bg: 'bg-secondary/10', text: 'text-secondary' };
  };

  const colors = getColor();

  // SVG circle parameters
  const size = 160;
  const strokeWidth = 12;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percentage / 100) * circumference;

  // Calculate estimated cost
  const getEstimatedCost = () => {
    if (current <= limit) return 0;
    return ((current - limit) / 1000) * 5; // $5 per 1000 over limit
  };

  const estimatedCost = getEstimatedCost();

  // Calculate projections
  const today = new Date();
  const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
  const dayOfMonth = today.getDate();
  const daysRemaining = daysInMonth - dayOfMonth;

  // Projected monthly usage based on daily average
  const projectedMonthly = dailyAverage > 0 ? Math.round(dailyAverage * daysInMonth) : 0;
  const projectedEndOfMonth = current + Math.round(dailyAverage * daysRemaining);
  const projectedPercentage = (projectedMonthly / limit) * 100;

  // Days until limit reached
  const daysUntilLimit = dailyAverage > 0 ? Math.floor((limit - current) / dailyAverage) : Infinity;

  // Color for projection
  const getProjectionColor = () => {
    if (projectedPercentage >= 100) return 'text-error';
    if (projectedPercentage >= 80) return 'text-primary';
    if (projectedPercentage >= 50) return 'text-primary-fixed-dim';
    return 'text-secondary';
  };

  return (
    <div className="bg-surface-container-highest/60 backdrop-blur-sm rounded-2xl p-6 shadow-[0px_12px_32px_rgba(0,0,0,0.2)]">
      <h3 className="text-on-surface font-headline mb-4">Mapbox API Usage</h3>

      <div className="flex items-center justify-center">
        <div className="relative">
          <svg width={size} height={size} className="transform -rotate-90">
            {/* Background circle */}
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke="rgba(255,255,255,0.05)"
              strokeWidth={strokeWidth}
            />
            {/* Progress circle */}
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke={colors.stroke}
              strokeWidth={strokeWidth}
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              strokeLinecap="round"
              className="transition-all duration-500"
            />
          </svg>

          {/* Center text */}
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className={`text-3xl font-bold ${colors.text}`}>
              {percentage.toFixed(1)}%
            </span>
            <span className="text-slate-500 text-sm">used</span>
          </div>
        </div>
      </div>

      <div className="mt-4 space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-on-surface-variant font-label">Current</span>
          <span className="text-on-surface font-medium">{current.toLocaleString()} loads</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-on-surface-variant font-label">Free Tier Limit</span>
          <span className="text-on-surface font-medium">{limit.toLocaleString()} loads</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-on-surface-variant font-label">Remaining</span>
          <span className={`font-medium ${current > limit ? 'text-error' : 'text-secondary'}`}>
            {Math.max(0, limit - current).toLocaleString()} loads
          </span>
        </div>
        {estimatedCost > 0 && (
          <div className="flex justify-between text-sm pt-2">
            <span className="text-error">Estimated Overage Cost</span>
            <span className="text-error font-bold">${estimatedCost.toFixed(2)}</span>
          </div>
        )}
      </div>

      {/* Projection Section */}
      {dailyAverage > 0 && (
        <div className="mt-4 pt-4 space-y-2">
          <h4 className="text-on-surface-variant text-xs font-label uppercase tracking-wide mb-2">
            Projections (based on {daysTracked} day{daysTracked !== 1 ? 's' : ''} of data)
          </h4>
          <div className="flex justify-between text-sm">
            <span className="text-on-surface-variant font-label">Daily Average</span>
            <span className="text-on-surface font-medium">{dailyAverage.toFixed(1)} loads/day</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-on-surface-variant font-label">Projected This Month</span>
            <span className={`font-medium ${getProjectionColor()}`}>
              {projectedEndOfMonth.toLocaleString()} loads
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-on-surface-variant font-label">Full Month Estimate</span>
            <span className={`font-medium ${getProjectionColor()}`}>
              {projectedMonthly.toLocaleString()} loads ({projectedPercentage.toFixed(1)}%)
            </span>
          </div>
          {daysUntilLimit < 365 && daysUntilLimit > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-on-surface-variant font-label">Days Until Limit</span>
              <span className={`font-medium ${daysUntilLimit < 30 ? 'text-primary' : 'text-secondary'}`}>
                {daysUntilLimit} days
              </span>
            </div>
          )}
          {projectedEndOfMonth > limit && (
            <div className="flex justify-between text-sm pt-2">
              <span className="text-error">Projected Overage Cost</span>
              <span className="text-error font-bold">
                ${(((projectedEndOfMonth - limit) / 1000) * 5).toFixed(2)}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Warning messages */}
      {(percentage >= 80 || projectedPercentage >= 100) && (
        <div className={`mt-4 p-3 rounded-lg ${projectedPercentage >= 100 ? 'bg-error/10' : colors.bg}`}>
          <p className={`text-sm ${projectedPercentage >= 100 ? 'text-red-400' : colors.text}`}>
            {percentage >= 95
              ? '🚨 Critical: Almost at limit! Consider upgrading or reducing usage.'
              : projectedPercentage >= 100
                ? `⚠️ Warning: At current rate, you'll exceed the free tier this month!`
                : percentage >= 80
                  ? '⚠️ Warning: Approaching free tier limit.'
                  : ''}
          </p>
        </div>
      )}
    </div>
  );
};

export default MapboxUsageGauge;
